import { z } from "zod";

import { addCustomFieldIssues, customFieldsSchema } from "components/BaseForm";

const isFilled = value => value.trim() !== "";
const isEmail = value => z.string().email().safeParse(value).success;

const emailSchema = z.object({
  id: z.number().optional(),
  email: z
    .string()
    .trim()
    .refine(value => !value || isEmail(value), "Enter a valid email address"),
  is_primary: z.boolean(),
  enabled: z.boolean(),
  subscribed: z.boolean(),
  domain_status: z.string().nullable().optional(),
  disabled_reason: z.string().nullable().optional(),
});

const phoneSchema = z.object({
  id: z.number().optional(),
  number: z.string().trim(),
  ext: z.string().trim(),
  type: z.number(),
  is_primary: z.boolean(),
  is_callable: z.boolean(),
  enabled: z.boolean(),
});

const addIssue = (ctx, path, message) =>
  ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });

// The message needs a field that renders errors, so it goes on the first email
// row, or on the first phone row when every email row was removed.
const contactMethodPath = values => {
  if (values.email_addresses.length) return ["email_addresses", 0, "email"];
  if (values.phone_numbers.length) return ["phone_numbers", 0, "number"];
  return ["full_name"];
};

/**
 * The Contact form schema. The API rejects a contact with neither an email nor
 * a phone, so the form checks that before the request.
 */
export const buildContactSchema = ({ customFieldConfigs }) =>
  z
    .object({
      full_name: z.string().trim().min(1, "Full name is required"),
      preferred_name: z.string().trim(),
      title: z.string().trim(),
      entity_id: z.number({
        required_error: "Entity is required",
        invalid_type_error: "Entity is required",
      }),
      department_id: z.number().nullable(),
      email_addresses: z.array(emailSchema),
      phone_numbers: z.array(phoneSchema),
      alternate_names: z.array(z.string()),
      tags: z.array(z.any()),
      custom_fields: customFieldsSchema,
    })
    .superRefine((values, ctx) => {
      const filledEmails = values.email_addresses
        .map((row, index) => ({ row, index }))
        .filter(({ row }) => isFilled(row.email));
      const hasPhone = values.phone_numbers.some(row => isFilled(row.number));

      if (!filledEmails.length && !hasPhone) {
        addIssue(
          ctx,
          contactMethodPath(values),
          "Provide an email or a phone number"
        );
      }

      // Other features send to the primary address, so one must be marked
      // whenever any address is filled.
      if (
        filledEmails.length &&
        !filledEmails.some(({ row }) => row.is_primary)
      ) {
        addIssue(
          ctx,
          ["email_addresses", filledEmails[0].index, "email"],
          "Mark one email as primary"
        );
      }

      addCustomFieldIssues(customFieldConfigs, values.custom_fields, ctx);
    });
